import type { LessonModel } from '../Models/LessonModel';
import type { StudentSlotModel } from '../Models/StudentModel';
import { GoogleCalendarService } from './GoogleCalendarService';
import { LessonService } from './LessonService';
import { StudentService } from './StudentService';
import { isCancelled } from '../Utils/LessonStatusUtil';

/**
 * Keeping Google Calendar in step as the tutor works, rather than only when
 * they open the sync screen and press a button.
 *
 * Every entry point here obeys the same three rules:
 *
 *   It is best effort. Saving a lesson must succeed whether or not Google is
 *   reachable, so nothing here is awaited by the code that saves, and nothing
 *   here throws. A calendar that is briefly behind is a far better outcome
 *   than a lesson that failed to save because a third party was down.
 *
 *   It never prompts. A token is taken only if one can be had silently, so a
 *   background sync can never throw a consent popup in front of somebody who
 *   was in the middle of typing. Not connected simply means nothing happens.
 *
 *   It is idempotent per record. A lesson with an event id is patched, one
 *   without gets a new event, and the id comes straight back to be stored, so
 *   the next edit patches rather than creating a duplicate.
 *
 *   A cancelled lesson is not an appointment. Its event is deleted and the
 *   link cleared, so it disappears from the tutor's phone the same way a
 *   deleted lesson does; undoing the cancellation creates a fresh event.
 */

/** What the caller should now store as the lesson's event id, if anything. */
export interface LinkChange { gcalEventId: string | null }

async function connected(): Promise<boolean> {
  try {
    return await GoogleCalendarService.ensureSilentToken();
  } catch {
    return false;
  }
}

function report(what: string, error: unknown): void {
  console.warn(`[gcal] ${what} did not reach the calendar`, error);
}

export const CalendarAutoSync = {
  /**
   * Pushes a created, edited or re-statused lesson.
   *
   * Resolves with the link the caller should now store when it changed: a
   * new event id after a create, null after a cancellation removed the event.
   * Resolves with null when there was nothing to store. Linking is done here
   * rather than by the caller so the two can never come apart.
   */
  async lessonSaved(lesson: LessonModel, userId: string): Promise<LinkChange | null> {
    if (!userId || !await connected()) return null;
    try {
      if (isCancelled(lesson)) {
        if (!lesson.gcalEventId) return null;
        await GoogleCalendarService.deleteEvent(lesson.gcalEventId);
        await LessonService.linkCalendarEvent(lesson.id, null);
        return { gcalEventId: null };
      }
      if (lesson.gcalEventId) {
        await GoogleCalendarService.updateLessonEvent(lesson.gcalEventId, lesson, userId);
        return null;
      }
      const eventId = await GoogleCalendarService.createLessonEvent(lesson, userId);
      if (!eventId) return null;
      await LessonService.linkCalendarEvent(lesson.id, eventId);
      return { gcalEventId: eventId };
    } catch (error) {
      report('a lesson', error);
      return null;
    }
  },

  /** Removes the event for a deleted lesson. */
  async lessonRemoved(lesson: Pick<LessonModel, 'gcalEventId'>): Promise<void> {
    if (!lesson.gcalEventId || !await connected()) return;
    try {
      await GoogleCalendarService.deleteEvent(lesson.gcalEventId);
    } catch (error) {
      report('a deleted lesson', error);
    }
  },

  /** Removes the events for lessons cleared in bulk. */
  async lessonsRemoved(lessons: readonly Pick<LessonModel, 'gcalEventId'>[]): Promise<void> {
    const ids = lessons.map(lesson => lesson.gcalEventId).filter((id): id is string => Boolean(id));
    if (ids.length === 0 || !await connected()) return;
    for (const id of ids) {
      try {
        await GoogleCalendarService.deleteEvent(id);
      } catch (error) {
        report('a cleared lesson', error);
      }
    }
  },

  /** Pushes a created or edited recurring slot. Resolves with a new event id to store. */
  async slotSaved(student: string, slot: StudentSlotModel, userId: string): Promise<string | null> {
    if (!userId || !await connected()) return null;
    const input = {
      slotId: slot.id,
      student,
      weekday: Number(slot.weekday),
      time: slot.time,
      durationMinutes: Number(slot.durationMinutes)
    };
    try {
      if (slot.gcalEventId) {
        await GoogleCalendarService.updateSlotEvent(slot.gcalEventId, input, userId);
        return null;
      }
      const eventId = await GoogleCalendarService.createSlotEvent(input, userId);
      if (eventId) await StudentService.linkSlotCalendarEvent(slot.id, eventId);
      return eventId;
    } catch (error) {
      report('a recurring slot', error);
      return null;
    }
  },

  async slotRemoved(slot: Pick<StudentSlotModel, 'gcalEventId'>): Promise<void> {
    if (!slot.gcalEventId || !await connected()) return;
    try {
      await GoogleCalendarService.deleteEvent(slot.gcalEventId);
    } catch (error) {
      report('a deleted recurring slot', error);
    }
  }
};
